import { useState, useEffect, useRef, useCallback } from 'react'
import { Plus, LayoutGrid, LayoutList, Heart, MessageCircle, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Badge } from '@/components/ui/badge'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCreatorPosts, useCreatorPostStats } from '@/hooks/use-creators'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { PostFormDialog } from './post-form-dialog'
import { PostCard, type PostCardData } from '@/components/shared/post-card'
import type { CreatorPost } from '@/lib/api/types'

// Column count based on container width (reacts to sidebar toggle + window resize)
const COL_BREAKPOINTS = [
  { minWidth: 1000, cols: 5 },
  { minWidth: 780, cols: 4 },
  { minWidth: 520, cols: 3 },
  { minWidth: 0, cols: 2 },
]

function useContainerColumns<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [cols, setCols] = useState(2)

  const updateCols = useCallback((width: number) => {
    const match = COL_BREAKPOINTS.find((bp) => width >= bp.minWidth)
    setCols(match?.cols ?? 2)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) updateCols(entry.contentRect.width)
    })

    observer.observe(el)
    updateCols(el.clientWidth)

    return () => observer.disconnect()
  }, [updateCols])

  return { ref, cols }
}

function toPostCardData(post: CreatorPost, authorUsername: string): PostCardData {
  return {
    id: post.id,
    authorUsername,
    platform: post.platform,
    mediaType: post.mediaType ?? null,
    postType: post.postType,
    caption: post.caption ?? null,
    postUrl: post.postUrl ?? null,
    mediaUrl: post.mediaUrl ?? null,
    thumbnailUrl: post.thumbnailUrl ?? null,
    r2MediaKey: post.r2MediaKey ?? null,
    carouselMedia: post.carouselMedia ?? null,
    mediaWidth: null,
    mediaHeight: null,
    likes: post.likes ?? null,
    comments: post.comments ?? 0,
    views: post.views ?? 0,
    postedAt: post.postedAt ?? null,
    createdAt: post.createdAt,
  }
}

interface PostsTabProps {
  creatorId: string
  authorUsername: string
}

/**
 * Renders the posts overview tab for a creator, showing KPI cards, view controls, and the creator's posts in grid or list layout.
 *
 * Fetches posts and aggregate stats for the given creatorId, displays loading/empty states, and provides controls to switch views and open the post registration dialog.
 *
 * @param creatorId - The creator's identifier used to load posts and statistics
 * @returns A React element containing the posts overview UI (KPIs, controls, posts list/grid, and the PostFormDialog)
 */
export function PostsTab({ creatorId, authorUsername }: PostsTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const { ref: gridRef, cols: columnCount } = useContainerColumns<HTMLDivElement>()

  const { data: postsResult, isLoading } = useCreatorPosts(creatorId)
  const { data: stats } = useCreatorPostStats(creatorId)

  const posts = postsResult?.data || []

  // Platform styling mapping (used only by list view)
  const platformConfig: Record<string, { bg: string; label: string }> = {
    instagram: {
      bg: 'bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white border-0',
      label: 'Instagram',
    },
    tiktok: { bg: 'bg-black text-white border-0', label: 'TikTok' },
  }

  return (
    <div ref={gridRef} className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Total de Posts</h4>
          <div className="text-3xl font-bold">{stats?.totalPosts || 0}</div>
        </div>
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Últimos 30 Dias</h4>
          <div className="text-3xl font-bold">{stats?.last30Days || 0}</div>
        </div>
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 md:col-span-2">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            Engajamento Total Estimado
          </h4>
          <div className="text-3xl font-bold">
            {posts
              .reduce((acc, p) => acc + (p.likes ?? 0) + (p.comments || 0), 0)
              .toLocaleString('pt-BR')}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              Interações mapeadas
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2 p-1 border rounded-md">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setViewMode('list')}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
        </div>

        <ChonkyButton onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Registrar Post
        </ChonkyButton>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">
          Carregando histórico de posts...
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-md border p-12 text-center border-dashed">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mb-4">
            <LayoutGrid className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-medium mb-1">Nenhum post registrado</h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Este influencer ainda não tem publicações vinculadas à marca no sistema.
          </p>
          <ChonkyButton variant="secondary" className="mt-4" onClick={() => setIsDialogOpen(true)}>
            Adicionar Primeiro Post
          </ChonkyButton>
        </div>
      ) : viewMode === 'grid' ? (
        /* Masonry grid — same layout as MentionFeed */
        <div className="flex gap-3 min-w-0">
          {Array.from({ length: columnCount }, (_, colIdx) => (
            <div key={colIdx} className="flex-1 min-w-0 flex flex-col gap-3">
              {posts
                .filter((_, i) => i % columnCount === colIdx)
                .map((post) => (
                  <PostCard key={post.id} post={toPostCardData(post, authorUsername)} />
                ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => {
            const config = platformConfig[post.platform] || {
              bg: 'bg-primary text-primary-foreground',
              label: post.platform,
            }

            // List View Render
            return (
              <div
                key={post.id}
                className="flex gap-4 p-4 rounded-lg border bg-card text-card-foreground shadow-sm items-center"
              >
                <div className="h-16 w-16 shrink-0 rounded bg-muted flex items-center justify-center overflow-hidden">
                  {post.thumbnailUrl ? (
                    <img
                      src={post.thumbnailUrl}
                      alt="Thumb"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-xs uppercase">{post.postType}</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className={`text-[10px] ${config.bg}`}>
                      {config.label}
                    </Badge>
                    <span className="text-sm font-medium capitalize">{post.postType}</span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {post.caption || 'Sem legenda'}
                  </p>
                </div>

                <div className="flex gap-4 text-sm text-muted-foreground mr-4">
                  <div className="flex flex-col items-center">
                    <Heart className="h-4 w-4 mb-1" />
                    {post.likes === null ? (
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default">–</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Creator deixou as curtidas ocultas
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span>{post.likes || 0}</span>
                    )}
                  </div>
                  <div className="flex flex-col items-center">
                    <MessageCircle className="h-4 w-4 mb-1" />
                    <span>{post.comments || '-'}</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <Eye className="h-4 w-4 mb-1" />
                    <span>{post.views || '-'}</span>
                  </div>
                </div>

                <div className="shrink-0 flex flex-col items-end gap-2 border-l pl-4">
                  <span className="text-xs text-muted-foreground">
                    {post.postedAt
                      ? format(new Date(post.postedAt), 'dd MMM, yyyy', { locale: ptBR })
                      : '-'}
                  </span>
                  {post.postUrl && (
                    <a
                      href={post.postUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Abrir Link &rarr;
                    </a>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <PostFormDialog creatorId={creatorId} open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  )
}
